import {
  useCallback,
  useLayoutEffect,
  useState,
  useSyncExternalStore,
} from "react";

import { isAuthenticated } from "@/lib/api";

export function useIsAuthenticated() {
  const [isAuthed, setIsAuthed] = useState(false);

  useLayoutEffect(() => {
    isAuthenticated()
      .then((isAuthed) => setIsAuthed(isAuthed))
      .catch(console.error);
  }, []);

  return isAuthed;
}

/**
 * A boolean remembered in this browser, for per-viewer conveniences.
 *
 * Built on useSyncExternalStore rather than useState + useEffect. The app is
 * server-rendered, so reading localStorage during render would make the first
 * client render disagree with the HTML the server sent; `getServerSnapshot`
 * returns "nothing remembered" and React swaps in the real value after
 * hydration, with no mismatch and no setState inside an effect.
 *
 * Every access is wrapped, and there is an in-memory fallback behind it:
 * localStorage throws outright in private windows and wherever site data is
 * blocked. Losing the memory there is fine; losing the control is not, so the
 * flag still works for that page view, it just does not survive a reload.
 */
const flagListeners = new Set<() => void>();
const flagMemory = new Map<string, string>();

function readFlag(key: string) {
  try {
    const stored = window.localStorage.getItem(key);
    if (stored !== null) return stored;
  } catch {
    // Unreachable storage; fall through to whatever this page view has set.
  }
  return flagMemory.get(key) ?? null;
}

function writeFlag(key: string, value: string) {
  // Memory first, so the control keeps working even when the write below throws.
  flagMemory.set(key, value);
  try {
    window.localStorage.setItem(key, value);
  } catch {
    // Best effort: remembered for this page view only.
  }
  for (const listener of flagListeners) listener();
}

/**
 * The same storage as `useStoredFlag`, holding a string rather than a boolean.
 *
 * Written for "when did you last look at your notifications". Per-browser, which is a real
 * limitation -- look on your laptop and the badge is still there on your phone -- but the
 * alternative is a column and a migration for a dot, and the notifications themselves are
 * derived from the audit log on the server, so nothing is lost by forgetting this.
 */
export function useStoredString(key: string) {
  const subscribe = useCallback((onStoreChange: () => void) => {
    flagListeners.add(onStoreChange);
    window.addEventListener("storage", onStoreChange);
    return () => {
      flagListeners.delete(onStoreChange);
      window.removeEventListener("storage", onStoreChange);
    };
  }, []);

  const stored = useSyncExternalStore(
    subscribe,
    useCallback(() => readFlag(key), [key]),
    () => null,
  );

  const store = useCallback((next: string) => writeFlag(key, next), [key]);

  return [stored, store] as const;
}

export function useStoredFlag(key: string, fallback = false) {
  const subscribe = useCallback((onStoreChange: () => void) => {
    flagListeners.add(onStoreChange);
    // `storage` only fires in OTHER tabs, which is why flagListeners exists.
    window.addEventListener("storage", onStoreChange);
    return () => {
      flagListeners.delete(onStoreChange);
      window.removeEventListener("storage", onStoreChange);
    };
  }, []);

  const stored = useSyncExternalStore(
    subscribe,
    useCallback(() => readFlag(key), [key]),
    () => null,
  );

  const store = useCallback((next: boolean) => writeFlag(key, String(next)), [key]);

  return [stored === null ? fallback : stored === "true", store] as const;
}
